// Căutarea globală: începi să scrii un ticker oriunde în screener și apare fereastra.
//
// Ideea e că nu trebuie să cauți întâi câmpul de căutare. Orice tastă literă deschide
// fereastra la centru cu litera deja înăuntru, săgețile aleg, Enter deschide graficul.
//
// Prinderea tastelor la nivel de document e delicată: dacă nu ne dăm la o parte când
// utilizatorul scrie într-un câmp obișnuit, îi furăm literele.

import { el, clear, bus, fmtVolume } from '../core/util.js';
import { iconMoneda } from './coin-icon.js';
import { icon } from './icons.js';
import { market } from '../core/state.js';

/** Ce se scrie pe o tastatură rusească când tasta latină e apăsată. */
const RU_TO_EN = {
  й: 'q', ц: 'w', у: 'e', к: 'r', е: 't', н: 'y', г: 'u', ш: 'i', щ: 'o', з: 'p',
  ф: 'a', ы: 's', в: 'd', а: 'f', п: 'g', р: 'h', о: 'j', л: 'k', д: 'l',
  я: 'z', ч: 'x', с: 'c', м: 'v', и: 'b', т: 'n', ь: 'm',
};

/** «ИЕС» → «BTC»: tickerele sunt latine, dar tastatura poate fi pe altă limbă. */
function normalize(text) {
  return [...text.toLowerCase()].map((c) => RU_TO_EN[c] || c).join('').toUpperCase();
}

const MAX = 8;

export class GlobalSearch {
  #open = false;

  constructor(app) {
    this.app = app;
    document.addEventListener('keydown', (e) => this.#onKey(e));
  }

  #typingElsewhere(target) {
    const tag = target?.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
  }

  #onKey(e) {
    if (this.#open) return;                       // fereastra își tratează singură tastele
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (this.#typingElsewhere(e.target)) return;
    if (!location.pathname.startsWith('/screener')) return;
    // O singură literă sau cifră deschide căutarea; tastele de unelte rămân ale graficului.
    if (e.key.length !== 1 || !/[a-zA-Z0-9а-яА-Я]/.test(e.key)) return;
    // Deasupra unui grafic litera aparține uneltelor de desen — acolo desenezi, nu cauți.
    if (document.querySelector('.chart-root:hover')) return;

    e.preventDefault();
    this.open(e.key);
  }

  open(seed = '') {
    if (this.#open) return;
    this.#open = true;
    this.index = 0;

    this.inputEl = el('input', {
      class: 'gsearch-input',
      type: 'text',
      placeholder: 'Тикер монеты…',
      value: seed,
      oninput: () => this.#render(),
      onkeydown: (e) => this.#onInputKey(e),
    });

    this.listEl = el('div', { class: 'gsearch-list' });
    this.boxEl = el('div', { class: 'gsearch-box' }, [
      el('div', { class: 'gsearch-head' }, [icon('search', 13), this.inputEl]),
      this.listEl,
      el('div', { class: 'gsearch-hint' }, [
        el('span', { text: '↑↓ выбрать' }),
        el('span', { text: 'Enter открыть' }),
        el('span', { text: 'Esc закрыть' }),
      ]),
    ]);

    this.rootEl = el('div', {
      class: 'gsearch',
      onclick: (e) => { if (e.target === this.rootEl) this.close(); },
    }, [this.boxEl]);

    document.body.append(this.rootEl);
    this.inputEl.focus();
    this.inputEl.setSelectionRange(seed.length, seed.length);
    this.#render();
  }

  #matches() {
    const q = normalize(this.inputEl.value.trim());
    if (!q) return [];
    // Simbolul e cheia hărții, nu un câmp al rândului — de aceea îl luăm din intrări.
    const rows = market.rows ? [...market.rows].map(([symbol, r]) => ({ ...r, symbol })) : [];
    const scor = (r) => {
      const s = r.symbol;
      if (s === q || s === `${q}USDT`) return 0;   // potrivire exactă
      if (s.startsWith(q)) return 1;               // început de ticker
      if (s.includes(q)) return 2;
      return 99;
    };
    return rows
      .map((r) => ({ r, s: scor(r) }))
      .filter((x) => x.s < 99)
      // La scor egal contează lichiditatea: cine caută „BTC" vrea perechea grasă.
      .sort((a, b) => a.s - b.s || (b.r.quoteVolume || 0) - (a.r.quoteVolume || 0))
      .slice(0, MAX)
      .map((x) => x.r);
  }

  #render() {
    clear(this.listEl);
    this.rezultate = this.#matches();
    this.index = Math.min(this.index, Math.max(0, this.rezultate.length - 1));

    if (!this.rezultate.length) {
      this.listEl.append(el('div', { class: 'gsearch-empty', text: 'Ничего не найдено' }));
      return;
    }

    this.rezultate.forEach((row, i) => {
      const pct = Number(row.change24h || 0);
      this.listEl.append(el('button', {
        class: `gsearch-item${i === this.index ? ' active' : ''}`,
        // `mouseenter`, nu: lista apare chiar sub cursorul nemișcat și ar muta singură
        // selecția pe rândul de dedesubt. `mousemove` cere o mișcare adevărată.
        onmousemove: () => { if (this.index !== i) { this.index = i; this.#syncActive(); } },
        onclick: () => this.#choose(row.symbol),
      }, [
        iconMoneda(row.symbol, 17),
        el('span', { class: 'gsearch-sym', text: row.symbol.replace(/USDT$/, '') }),
        el('span', { class: 'gsearch-vol', text: fmtVolume(row.quoteVolume || 0) }),
        el('span', {
          class: `gsearch-pct ${pct >= 0 ? 'up' : 'down'}`,
          text: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
        }),
      ]));
    });
  }

  #syncActive() {
    [...this.listEl.children].forEach((c, i) => c.classList.toggle('active', i === this.index));
  }

  #onInputKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = this.rezultate?.length || 0;
      if (!n) return;
      this.index = (this.index + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
      this.#syncActive();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = this.rezultate?.[this.index];
      if (row) this.#choose(row.symbol);
    }
  }

  #choose(symbol) {
    this.close();
    bus.emit('symbol:open', { symbol });
  }

  close() {
    if (!this.#open) return;
    this.#open = false;
    this.rootEl?.remove();
    this.rootEl = null;
  }
}
